// Action types

import { useMemo } from 'react';
import { Action, ActionCreator, bindActionCreators } from 'redux';

import { BookingTypeValues } from '@components/BookingType';
import useStore from '@hooks/useStore';
import { Middleware } from '@spazious/react-hooks';

import {
  SearchFormDestinationDollhouseHotel,
  SearchFormDestinationDollhouseLocation,
  SearchFormDestinationHotel,
  SearchFormDestinationLocation,
} from './SearchFormTypes.js';

export enum SearchFormActionKind {
  SetBookingType = 'SetBookingType',
  SetDestination = 'SetDestination',
  SetDateRange = 'SetDateRange',
  SetCapacityOrRoomCount = 'SetCapacityOrRoomCount',
  SetFormData = 'SetFormData',
  SetState = 'SetState',
  DoSearch = 'DoSearch',
  ResetFormData = 'ResetFormData',
}
export interface SearchFormSetBookingTypeAction
  extends Action<SearchFormActionKind.SetBookingType> {
  payload: BookingTypeValues;
}

export interface SearchFormSetDestinationAction
  extends Action<SearchFormActionKind.SetDestination> {
  payload: SearchFormDestinationHotel | SearchFormDestinationLocation | null;
}

export interface SearchFormSetDateRangeAction
  // `{startDate}/{endDate}`
  extends Action<SearchFormActionKind.SetDateRange> {
  payload: string | null;
}

export interface SearchFormSetCapacityOrRoomCountAction
  extends Action<SearchFormActionKind.SetCapacityOrRoomCount> {
  payload: number;
}

export interface SearchFormSetFormDataAction extends Action<SearchFormActionKind.SetFormData> {
  payload: Partial<Omit<SearchFormState, 'destination'>>;
}

export interface SearchFormSetStateAction extends Action<SearchFormActionKind.SetState> {
  payload: Partial<SearchFormState>;
}

export interface SearchFormDoSearchAction extends Action<SearchFormActionKind.DoSearch> {}

export interface SearchFormResetFormDataAction extends Action<SearchFormActionKind.ResetFormData> {}

export type SearchFormActions =
  | SearchFormSetBookingTypeAction
  | SearchFormSetDestinationAction
  | SearchFormSetDateRangeAction
  | SearchFormSetCapacityOrRoomCountAction
  | SearchFormSetFormDataAction
  | SearchFormSetStateAction
  | SearchFormResetFormDataAction
  | SearchFormDoSearchAction;

// Action creators
export const setBookingType: ActionCreator<SearchFormSetBookingTypeAction> = (payload) => ({
  type: SearchFormActionKind.SetBookingType,
  payload,
});

export const setDestination: ActionCreator<SearchFormSetDestinationAction> = (payload) => ({
  type: SearchFormActionKind.SetDestination,
  payload,
});

export const setDateRange: ActionCreator<SearchFormSetDateRangeAction> = (payload) => ({
  type: SearchFormActionKind.SetDateRange,
  payload,
});

export const setCapacityOrRoomCount: ActionCreator<SearchFormSetCapacityOrRoomCountAction> = (
  payload
) => ({
  type: SearchFormActionKind.SetCapacityOrRoomCount,
  payload,
});

export const setFormData: ActionCreator<SearchFormSetFormDataAction> = (payload) => ({
  type: SearchFormActionKind.SetFormData,
  payload,
});

export const setState: ActionCreator<SearchFormSetStateAction> = (payload) => ({
  type: SearchFormActionKind.SetState,
  payload,
});

export const resetFormData: ActionCreator<SearchFormResetFormDataAction> = (payload) => ({
  type: SearchFormActionKind.ResetFormData,
  payload,
});

export const doSearch: ActionCreator<SearchFormDoSearchAction> = (payload) => ({
  type: SearchFormActionKind.DoSearch,
  payload,
});

export const searchFormActionCreators = {
  setBookingType,
  setDestination,
  setDateRange,
  setCapacityOrRoomCount,
  setFormData,
  setState,
  resetFormData,
  doSearch,
};

// State
export interface SearchFormState {
  destination:
    | SearchFormDestinationHotel
    | SearchFormDestinationLocation
    | SearchFormDestinationDollhouseHotel
    | SearchFormDestinationDollhouseLocation
    | null;
  dateRange: string | null;
  capacityOrRoomCount: number;
  bookingType: BookingTypeValues;
}

export const initialState: SearchFormState = {
  destination: null,
  dateRange: null,
  capacityOrRoomCount: 1,
  bookingType: BookingTypeValues.AccommodationAndSpaces,
};

export const searchFormReducer = (
  state: SearchFormState,
  action: SearchFormActions
): SearchFormState => {
  switch (action.type) {
    case SearchFormActionKind.SetBookingType:
      return {
        ...state,
        bookingType: action.payload,
      };

    case SearchFormActionKind.SetDestination:
      return {
        ...state,
        destination: action.payload,
      };
    case SearchFormActionKind.SetDateRange:
      return {
        ...state,
        dateRange: action.payload,
      };
    case SearchFormActionKind.SetCapacityOrRoomCount:
      return {
        ...state,
        capacityOrRoomCount: action.payload,
      };
    case SearchFormActionKind.SetFormData:
      return {
        ...state,
        ...action.payload,
      };
    case SearchFormActionKind.SetState:
      return {
        ...state,
        ...action.payload,
      };
    case SearchFormActionKind.ResetFormData:
      return { ...initialState };
    default:
      return state;
  }
};

export const useSearchFormStore = (
  partialInitialState?: Partial<SearchFormState>,
  middlewares: Middleware<SearchFormState, SearchFormActions>[] = []
) => {
  const store = useStore(
    searchFormReducer,
    {
      ...initialState,
      ...partialInitialState,
    },
    middlewares
  );

  const boundActionCreators = useMemo(
    () => bindActionCreators(searchFormActionCreators, store.dispatch),
    [store.dispatch]
  );

  return [store.state, boundActionCreators, store] as const;
};
